import '../../vendor/marked';
import { languages } from '../../constants/languages';
import * as EventUtils from '../../utils/event-utils';
import { codeHighlightComponentModule } from '../code-highlight/code-highlight';
import { zoomableImageComponentModule } from '../zoomable-image/zoomable-image';
import './markdown.less';
import { escapeHTML } from '../../utils/string-utils';

const KNOWN_HTML_ELEMENTS = new Set(
  `a,abbr,acronym,address,area,article,aside,b,bdi,bdo,big,blockquote,br,caption,center,cite,code,col,colgroup,dd,del,dfn,dir,div,dl,dt,em,figcaption,figure,font,footer,h1,h2,h3,h4,h5,h6,header,hgroup,hr,i,img,ins,kbd,label,li,map,mark,menu,nav,ol,p,pre,q,rp,rt,ruby,s,samp,section,small,span,strike,strong,sub,sup,table,tbody,td,tfoot,th,thead,time,tr,tt,u,ul,var,wbr`.split(
    ',',
  ),
);
const COMMENT_NODE = '!--';

const HTML_MATCHER = /<\/?([^\s>]+)[^>]*>/gi;

export const markdownComponentModule = angular
  .module('Q.components.markdown', [
    codeHighlightComponentModule,
    zoomableImageComponentModule,
  ])

  // The start of this code was copied from angular-marked

  .provider('marked', function () {
    const self = this;

    self.defaults = {
      gfm: true,
      tables: true,
      breaks: true,
      smartLists: true,
    };

    self.setOptions = function (opts) {
      angular.extend(self.defaults, opts);
    };

    self.$get = function ($window, highlightCode) {
      const m = $window.marked;

      if (!self.defaults.highlight) {
        self.defaults.highlight = highlightCode;
      }

      self.setOptions = m.setOptions;
      m.setOptions(self.defaults);

      if ($window.markedExtensions) {
        m.process = $window.markedExtensions.process;
      }

      return m;
    };
  })

  /**
   * @docType shared
   * @category directive
   * @name markdown
   * @example
   *   <div markdown>#A Sample Header
   *---
   *##Wooohooo a subheader
   *> "Always code as if the guy who ends up maintaining your code will be a violent psychopath who knows where you live."
   *
   ** List Item A
   ** List Item B
   ** List Item C
   *     </div>
   * @description
   * Transcludes some HTML and also re-uses it in a sample code block
   * It will be good to keep all the HTML examples of the components in this directive so they can all be changed from one central location
   * @returns Directive Definition Object {object}
   */
  .directive('markdown', function (marked, $sanitize, $timeout, $compile) {
    return {
      restrict: 'A',
      scope: {
        opts: '<',
        markdown: '<',
        language: '<',
        sanitize: '@',
        linkTarget: '@',
        linkRel: '@',
        afterRender: '&',
        expandable: '<',
        expanded: '<?',
        expandableInline: '<',
        maxLines: '<',
        multiLanguage: '<',
      },
      link: function (scope, el, attrs) {
        const node = el[0];
        let lastVal;

        el.addClass('markdown');
        if (scope.expandable) {
          scope.expanded = !!scope.expanded;
          el.addClass(
            'expandable ' + (scope.expanded ? 'expanded' : 'collapsed'),
          );
          if (scope.expandableInline) {
            el.addClass('expandable-inline');
          }
        }
        let lastExpandedState = scope.expanded;
        let expandingPromise;
        let $expandLink;
        let expandListener;
        const toggleExpandable = (val) => {
          const exp = (scope.expanded =
            val === undefined ? !scope.expanded : val);

          if (exp === lastExpandedState) return;
          lastExpandedState = exp;

          if (expandingPromise) $timeout.cancel(expandingPromise);

          el.removeClass(
            'expanding-init collapsing-init expanding collapsed expanded collapsed',
          );
          el.addClass(exp ? 'expanding-init' : 'collapsing-init');
          expandingPromise = $timeout(() => {
            el.removeClass(exp ? 'expanding-init' : 'collapsing-init');
            el.addClass(exp ? 'expanding' : 'collapsing');
            expandingPromise = $timeout(() => {
              el.removeClass(exp ? 'expanding' : 'collapsing');
              el.addClass(exp ? 'expanded' : 'collapsed');

              if ($expandLink) {
                $expandLink.text(scope.expanded ? 'Less' : 'More');
              }
            }, 210);
          });
        };

        if (scope.expandable && attrs.expanded) {
          scope.$watch('expanded', toggleExpandable);
        }

        _render(scope.markdown || el.text() || '');
        if (attrs.markdown !== undefined) {
          scope.$watch('markdown', _render);
        }

        scope.$watch('language', () => _render());

        function _shouldExpand(val) {
          return (
            scope.expandable &&
            ((!scope.maxLines && val.length > 200) ||
              el.children().length > (scope.maxLines || 2) ||
              el[0].querySelector('img,h1,h2,h3,ul,ol,table,pre'))
          );
        }

        function _render(val) {
          if (val || val === '') {
            lastVal = val;
          } else {
            val = lastVal || '';
          }

          const opts = scope.opts || {};
          const result = _process(val, scope.language, opts);
          let html = result.html();
          if (scope.sanitize !== 'false') {
            html = _sanitizeHTML(html);
          }
          el.html(html);

          if (scope.linkTarget !== 'false' || scope.linkRel !== 'false') {
            const anchors = el.find('a');
            if (scope.linkTarget !== 'false') {
              anchors.attr('target', scope.linkTarget || '_blank');
            }
            if (scope.linkRel !== 'false') {
              anchors.attr('rel', scope.linkRel || 'noopener');
            }
          }

          if (scope.multiLanguage) {
            _replaceLanguageBlocks(node, scope.language);
          }

          _compileZoomableImages(node, scope);

          // if we have more than 200 characters, more than two nodes, or images, make it expandable
          if (_shouldExpand(val)) {
            el.addClass('expandable');
            el.toggleClass('has-max-lines', !!scope.maxLines);
            if (scope.maxLines) {
              const children = el.children().removeClass('always-show');
              for (let i = 0; i < scope.maxLines; i++) {
                children.eq(i).addClass('always-show');
              }
            }
            if (!scope.expandableInline) {
              $expandLink = angular.element(
                '<a href class="expand-link">More</a>',
              );
              $expandLink.on('click', (event) => {
                EventUtils.stopEvent(event);
                toggleExpandable();
                scope.$apply();
              });
              const $p = angular.element('<p class="expand-link-wrapper">');
              $p.append($expandLink);
              el.append($p);
              $p.parent().children().eq(-2).addClass('last-markdown-node');
            } else if (!expandListener) {
              expandListener = (event) => {
                if (event.target && event.target.nodeName === 'A') return;
                EventUtils.stopEvent(event);
                toggleExpandable();
                scope.$apply();
              };
              el.on('click touch', expandListener);
            }
            toggleExpandable(scope.expanded);
          } else {
            $expandLink = null;
            el.removeClass('expandable');
            toggleExpandable(false);
            if (expandListener) {
              el.off('click touch', expandListener);
            }
          }

          scope.afterRender();
        }
      },
    };

    //// private functions

    /**
     * processes the markdown using the marked-extensions library, which will handle a number of details
     * such as handling @@doc tokens, if/if-not blocks, hidden non-relevant language code, and %doc blocks.
     * @param markdown
     * @param defaultLang
     * @param opts
     * @private
     */
    function _process(markdown, defaultLang, opts) {
      markdown = '' + markdown;
      if (marked.process) {
        const options = {
          marked: opts || {},
          lineNumbers: false, // we provide our own implementation instead
          language: defaultLang,
          filterLanguages: false,
          findLanguage: (language) =>
            languages[language] ? languages[language].id : null,
          languageWrapper: (code, language) => {
            return `<div class="language-tabset"><div class="language-tab language-${language}">${code}</div></div>`;
          },
          // NOTE: this isn't really doing anything until we add in support for loading scripts dynamically
          // via the scriptjs package. the extensions library also supports mermaid/CodeMirror.runMode - which also
          // need the scriptjs package loaded and referenced as an option before they can be used.
          // onLoaded: result => $timeout(() => result.afterRender(), 100)
        };
        const result = marked.process(marked, markdown, options);
        // make sure we include some whitespace around brs
        result.renderer.br = () => '<br>\n';
        return result;
      }

      // if markedExtensions are not loaded on the page, then just render using basic markdown without
      // any additional logic.
      return {
        html: () => marked(markdown, opts),
      };
    }

    /**
     * @name ReplaceLanguageBlocks
     * @docType shared
     * @category function
     * @description
     * Replaces sequential code blocks with wrapped blocks for hiding/showing languages
     * @param node {object}
     * @param lang {string}
     * @private
     */
    function _replaceLanguageBlocks(node, lang) {
      if (!lang) return;
      // walk the tree looking for sequential pre/code blocks,
      // then convert them into a set of tabs
      const tabsToGroup = node.querySelectorAll(
        '.language-tabset + .language-tabset',
      );

      angular.forEach(tabsToGroup, function (tab) {
        tab.previousElementSibling.appendChild(tab.firstChild);
        tab.parentNode.removeChild(tab);
      });

      // now, in every group that has multiple tabs, remove the non-current languages
      const tabsets = node.querySelectorAll('.language-tabset');
      angular.forEach(tabsets, function (tabset) {
        if (tabset.children.length > 1) {
          angular.forEach(
            tabset.querySelectorAll('.language-tab'),
            function (tab) {
              const $tab = angular.element(tab);
              if (!$tab.hasClass('language-' + lang)) {
                tab.parentNode.removeChild(tab);
              }
            },
          );
        }
      });
    }

    function _compileZoomableImages(node, scope) {
      if (scope.expandableInline) {
        // don't enable zoomable images for inline markdown
        return;
      }
      angular.forEach(node.querySelectorAll('img'), (img) => {
        // wrap standalone images to make them zoomable if necessary
        if (
          _nonTextSibling(img.previousSibling) &&
          _nonTextSibling(img.nextSibling)
        ) {
          $compile(
            angular
              .element(img)
              .wrap('<zoomable-image zoom-disabled="expandable && !expanded">')
              .parent(),
          )(scope.$new());
        }
      });
    }

    // Only returns true if the sibling is empty or a hard line break
    function _nonTextSibling(sibling) {
      return !sibling || (sibling.nodeName || '').toUpperCase() === 'BR';
    }

    function _sanitizeHTML(html) {
      // escape unknown HTML
      const escapedHTML = html.replace(HTML_MATCHER, (match, nodeName) => {
        // check for comments or known HTML elements
        if (
          nodeName.startsWith(COMMENT_NODE) ||
          KNOWN_HTML_ELEMENTS.has(nodeName)
        ) {
          return match;
        } else {
          return escapeHTML(match);
        }
      });
      // sanitize remaining HTML
      return $sanitize(escapedHTML);
    }
  });
